import React, { useState, useCallback, useEffect } from 'react';
import Layout from './components/Layout';
import StoryInputForm from './components/StoryInputForm';
import ScriptDisplay from './components/ScriptDisplay';
import LoadingSpinner from './components/LoadingSpinner';
import VideoDisplay from './components/VideoDisplay';
import SeamlessVideoDisplay from './components/SeamlessVideoDisplay';
import SaveOptions from './components/SaveOptions';
import { CreditDisplay } from './components/CreditDisplay';
import { DevPanel } from './components/DevPanel';
import { ServerStatus } from './components/ServerStatus';
import { EnvironmentStatus } from './components/EnvironmentStatus';
import { VideoModelSelector, type VideoModel } from './components/VideoModelSelector';
import { VideoProviderSelector, type VideoProvider } from './components/VideoProviderSelector';
import { generateStoryScript, generateVideoFromScript } from './services/geminiService';
import { VideoLength, StoryLanguage, StoryStructure } from './types';
import { QuotaService } from './services/quotaService';
import { logger, logScriptGeneration, logVideoGeneration, logUserAction } from './utils/logger';
import { getAvailability, getNotionHealth, AvailabilityStatus } from './services/availabilityService';
import { useHeaderOffset } from './hooks/useHeaderOffset';

const App: React.FC = () => {
  // Set up header offset for credit panel positioning
  useHeaderOffset();

  const [script, setScript] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [currentVideoLength, setCurrentVideoLength] = useState<VideoLength | number>(VideoLength.FULL_16S);
  const [currentTopic, setCurrentTopic] = useState<string>('');
  const [currentLanguage, setCurrentLanguage] = useState<StoryLanguage>(StoryLanguage.KOREAN);
  const [currentStructure, setCurrentStructure] = useState<StoryStructure>(StoryStructure.HOOK_TO_END);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState<boolean>(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoModel, setVideoModel] = useState<VideoModel>('auto');
  const [videoProvider, setVideoProvider] = useState<VideoProvider>('auto');

  // Availability states
  const [availability, setAvailability] = useState<AvailabilityStatus>({
    videoEnabled: false,
    videoReason: 'INIT',
    remaining: null,
    quotaTotal: null,
    quotaUsed: null
  });
  const [notionHealth, setNotionHealth] = useState<{ notionReady: boolean; missing: any } | null>(null);

  // Check availability on mount
  useEffect(() => {
    getAvailability().then(setAvailability);
    getNotionHealth().then(setNotionHealth);
  }, []);


  const handleGenerateScript = useCallback(async (topic: string, length: VideoLength | number, language: StoryLanguage, structure: StoryStructure) => {
    logUserAction('GENERATE_SCRIPT_START', { topic, length, language, structure });
    setIsLoading(true);
    setScript('');
    setError('');
    setVideoUrl(null);
    setVideoError(null);
    setCurrentVideoLength(length);
    setCurrentTopic(topic);
    setCurrentLanguage(language);
    setCurrentStructure(structure);
    try {
      const generatedScript = await generateStoryScript(topic, length, language, structure);
      if(generatedScript.startsWith('Error:')) {
        setError(generatedScript);
        setScript('');
        logScriptGeneration(topic, length, false, generatedScript);
      } else {
        setScript(generatedScript);
        logScriptGeneration(topic, length, true);
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred.';
      setError(`Failed to generate script: ${errorMessage}`);
      setScript('');
      logScriptGeneration(topic, length, false, errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleGenerateVideo = useCallback(async () => {
    if (!script) {
        setVideoError("Cannot generate a video without a script.");
        return;
    }
    
    setIsGeneratingVideo(true);
    setVideoUrl(null);
    setVideoError(null);

    try {
        const url = await generateVideoFromScript(script);
        setVideoUrl(url);
        // Force credit display refresh after video generation
        window.dispatchEvent(new Event('credit-update'));
    } catch(e) {
        const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred.';
        setVideoError(errorMessage);
    } finally {
        setIsGeneratingVideo(false);
    }
  }, [script]);

  const handleGenerateSegment = useCallback(async (segmentNumber: number, scriptContent: string): Promise<string> => {
    logger.info('SEGMENT_GENERATION_START', { segmentNumber, scriptLength: scriptContent?.length, model: videoModel, provider: videoProvider });
    
    try {
      const url = await generateVideoFromScript(scriptContent, segmentNumber, videoModel, videoProvider);
      logger.info('SEGMENT_GENERATION_SUCCESS', { segmentNumber, urlType: typeof url, model: videoModel, provider: videoProvider });
      logVideoGeneration(segmentNumber, true);
      return url;
    } catch(e) {
      const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred.';
      logger.error('SEGMENT_GENERATION_FAIL', { segmentNumber, error: errorMessage, model: videoModel, provider: videoProvider });
      logVideoGeneration(segmentNumber, false, errorMessage);
      throw new Error(`Failed to generate segment ${segmentNumber}: ${errorMessage}`);
    }
  }, [videoModel, videoProvider]);

  const WelcomeMessage = () => (
    <div className="mt-8 text-center bg-slate-800/40 p-8 rounded-2xl border border-slate-700">
        <h2 className="text-2xl font-bold text-slate-100 mb-2">Welcome to StoryShot AI</h2>
        <p className="text-slate-400 max-w-2xl mx-auto">
            Simply enter a topic for your video, choose a desired length, and let our AI craft a compelling YouTube Shorts script for you in seconds.
        </p>
    </div>
  );

  return (
    <Layout>
      <CreditDisplay />
      <DevPanel />
      <ServerStatus />
      <div className="max-w-4xl mx-auto">
        <EnvironmentStatus />
        <StoryInputForm onGenerate={handleGenerateScript} isLoading={isLoading} />
        
        {error && (
            <div className="mt-8 bg-red-900/50 border border-red-700 text-red-200 p-4 rounded-lg">
                <p className="font-bold">An error occurred:</p>
                <p>{error}</p>
            </div>
        )}

        {isLoading && <LoadingSpinner />}

        {!isLoading && !error && script && (
            <>
                <div className="mt-8 bg-slate-800/60 p-6 rounded-2xl border border-slate-700">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold text-white">생성된 스크립트</h3>
                        <p className="text-xs text-slate-400">텍스트를 직접 수정할 수 있습니다</p>
                    </div>
                    <textarea
                        value={script}
                        onChange={(e) => setScript(e.target.value)}
                        className="w-full bg-slate-900/70 p-4 rounded-lg text-slate-300 font-mono text-sm min-h-[300px] max-h-[500px] resize-y border border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="여기서 바로 편집하세요"
                    />
                </div>
                
                {/* Video Generation Settings */}
                <div className="mt-6 bg-slate-800/60 p-6 rounded-2xl border border-slate-700">
                    <h3 className="text-lg font-bold text-white mb-4">🎬 비디오 생성 설정</h3>
                    
                    {/* Provider Selector */}
                    <VideoProviderSelector
                        value={videoProvider}
                        onChange={setVideoProvider}
                        disabled={isGeneratingVideo}
                    />
                    
                    {/* Model Selector */}
                    <VideoModelSelector 
                        value={videoModel}
                        onChange={setVideoModel}
                        disabled={isGeneratingVideo}
                    />
                </div>
                
                <SeamlessVideoDisplay 
                    script={script}
                    videoLength={currentVideoLength}
                    onGenerateSegment={handleGenerateSegment}
                    topic={currentTopic}
                />
                <SaveOptions
                    topic={currentTopic}
                    script={script}
                    videoLength={currentVideoLength}
                    language={currentLanguage}
                    structure={currentStructure}
                />
            </>
        )}

        {!isLoading && !error && !script && <WelcomeMessage />}
      </div>
    </Layout>
  );
};

export default App;
